/**
 * Chit Fund Calculation Utilities
 * 
 * This file contains all calculation logic for the chit fund system.
 * All calculations are derived from stored data (no hardcoded values).
 */

// Extra penalty amount for previous winners (₹1,000)
export const WINNER_PENALTY = 1000;

/**
 * Calculate total chit value for a group
 * @param {number} maxMembers - Maximum number of members in the group
 * @param {number} monthlyContribution - Monthly contribution per member
 * @returns {number} Total chit value
 */
export const calculateTotalChitValue = (maxMembers, monthlyContribution) => {
  return maxMembers * monthlyContribution;
};

/**
 * Calculate monthly pool amount
 * Base collection + penalties from previous winners
 * @param {number} baseCollection - Base collection (maxMembers × contribution)
 * @param {number} previousWinnersCount - Number of members who have won before
 * @returns {number} Monthly pool amount
 */
export const calculateMonthlyPool = (baseCollection, previousWinnersCount) => {
  const penalties = previousWinnersCount * WINNER_PENALTY;
  return baseCollection + penalties;
};

/**
 * Calculate winner payout
 * Monthly pool - organizer commission
 * @param {number} monthlyPool - Monthly pool amount
 * @param {number} commission - Organizer commission for this group
 * @returns {number} Winner payout amount
 */
export const calculateWinnerPayout = (monthlyPool, commission) => {
  return monthlyPool - (commission || 0);
};

/**
 * Calculate member's monthly payment
 * Base contribution + penalty (if they've won before)
 * @param {number} baseContribution - Base monthly contribution
 * @param {boolean} hasWon - Whether the member has won before
 * @returns {number} Total monthly payment for the member
 */
export const calculateMemberPayment = (baseContribution, hasWon) => {
  return hasWon ? baseContribution + WINNER_PENALTY : baseContribution;
};

/**
 * Get payment breakdown for a member
 * @param {number} baseContribution - Base monthly contribution
 * @param {boolean} hasWon - Whether the member has won before
 * @returns {Object} Payment breakdown with base, penalty, and total
 */
export const getMemberPaymentBreakdown = (baseContribution, hasWon) => {
  const base = baseContribution;
  const penalty = hasWon ? WINNER_PENALTY : 0;
  const total = base + penalty;
  
  return {
    base,
    penalty,
    total,
    hasWon,
  };
};

/**
 * Get group financial summary
 * @param {Object} group - Group data object
 * @param {Array} members - Array of member objects
 * @returns {Object} Financial summary with all calculations
 */
export const getGroupFinancialSummary = (group, members) => {
  const maxMembers = group.maxMembers || 0;
  const contribution = group.contribution || 0;
  const currentMonth = group.currentMonth || 1;
  const commission = group.commission || 0; // Use group-specific commission
  const totalMoney = group.totalMoney || 0;
  const months = group.months || 0;
  
  // Count previous winners
  const previousWinners = members.filter(m => m.hasWon === true).length;
  
  // Calculate base collection (all members pay base contribution)
  const baseCollection = maxMembers * contribution;
  
  // Calculate monthly pool
  const monthlyPool = calculateMonthlyPool(baseCollection, previousWinners);
  
  // Calculate winner payout using group-specific commission
  const winnerPayout = calculateWinnerPayout(monthlyPool, commission);
  
  // Total chit value (use stored totalMoney if available, otherwise calculate)
  const totalChitValue = totalMoney || calculateTotalChitValue(maxMembers, contribution);
  
  return {
    currentMonth,
    totalChitValue,
    baseCollection,
    monthlyPool,
    organizerCommission: commission,
    winnerPayout,
    previousWinnersCount: previousWinners,
    penaltyAmount: previousWinners * WINNER_PENALTY,
    months,
  };
};

